import { Button, Space, Typography, Divider } from "antd";
import PropTypes from "prop-types";
import { useTranslation, Trans } from "react-i18next";

import { ASYNC_DELETE_MODEL } from "@/constants/Global";
import { useBatch } from "@/hooks/queries";
import { useAsyncActions } from "@/modules/async-actions";
import { getModelKey, isModelDeleteAsync } from "@/modules/model";
import { useSidePanel } from "@/modules/side-panel";
import { Alert } from "@/ui/alert";

const BatchDelete = ({ type, models, invalidateKeys = [] }) => {
  const { t } = useTranslation();
  const { setDeletingState } = useAsyncActions();
  const { closeSidePanel } = useSidePanel();

  const mutation = useBatch(type + "s", "delete", { invalidateKeys });

  // TODO
  // - convert `type` to `modelKey`
  // - use `isModelDeleteAsync(modelKey)` instead of `ASYNC_DELETE_MODEL`
  // - and then delete `ASYNC_DELETE_MODEL` constant
  const isAsyncDelete = ASYNC_DELETE_MODEL.includes(type);

  /**
   * Closes the current delete panel.
   *
   * @return {void}
   */
  function onClose() {
    closeSidePanel();
  }

  /**
   * Attempts to delete a model
   *
   * @return {void}
   */
  // TODO IMP-1217 fix require-await
  // eslint-disable-next-line require-await
  async function onDelete() {
    const data = {};
    data[type + "s"] = models.map((model) => model.id);

    models.forEach((model) => {
      const modelKey = getModelKey(type);
      if (isModelDeleteAsync(modelKey)) {
        setDeletingState(modelKey, model.id);
      }
    });

    mutation.mutate(
      { data },
      {
        onSuccess: function () {
          onClose();
        },
      }
    );
  }

  // Used for translation
  const modelsCount = models.length;
  let plural = "";
  modelsCount > 1 ? (plural = "s") : (plural = "");

  const ContentFragment = () => (
    <>
      <Alert
        display={mutation.isError}
        closable={{ afterClose: () => mutation.reset() }}
      />
      <Typography.Text>{models.count}</Typography.Text>
      <Trans i18nKey="global:batchDelete.message.label" count={modelsCount}>
        You will delete {{ modelsCount }} {{ plural }} with the batch Check the
        options below.
      </Trans>
      {isAsyncDelete && (
        <>
          <Divider />
          <Alert
            display={true}
            title={t("global:asyncBatchDeleteNotification")}
            type="warning"
            closable={false}
          />
        </>
      )}
    </>
  );

  const ExtraFragment = () => (
    <Space>
      <Button onClick={onClose}>{t("global:cancel")}</Button>
      <Button onClick={onDelete} loading={mutation.isPending} type="primary">
        {t("global:delete")}
      </Button>
    </Space>
  );

  return (
    <>
      <ContentFragment />
      <Divider />
      <ExtraFragment />
    </>
  );
};

BatchDelete.propTypes = {
  type: PropTypes.string.isRequired,
  models: PropTypes.array.isRequired,
  invalidateKeys: PropTypes.array,
};

export default BatchDelete;
